import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import SubscriptionService, {
  Subscription,
  SubscriptionPlan,
  Invoice,
} from '../services/subscriptions';
import CustomerService, { Customer } from '../services/customers';
import { formatAmount, yuanToCents } from '../utils/money';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const statusConfig: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '生效中' },
  trialing: { color: 'processing', text: '试用中' },
  past_due: { color: 'warning', text: '逾期' },
  paused: { color: 'default', text: '已暂停' },
  canceled: { color: 'default', text: '已取消' },
  cancelled: { color: 'default', text: '已取消' },
  incomplete: { color: 'warning', text: '未完成' },
};

const intervalText: Record<string, string> = {
  day: '每日',
  week: '每周',
  month: '每月',
  year: '每年',
};

const invoiceStatusConfig: Record<string, { color: string; text: string }> = {
  draft: { color: 'default', text: '草稿' },
  open: { color: 'processing', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
  uncollectible: { color: 'error', text: '无法收款' },
  void: { color: 'default', text: '已作废' },
};

const SubscriptionsPage: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subLoading, setSubLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm] = Form.useForm();

  // 编辑订阅
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [savingSub, setSavingSub] = useState(false);
  const [editForm] = Form.useForm();

  // 发票
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceActionId, setInvoiceActionId] = useState<string | null>(null);

  // 创建订阅
  const [createSubVisible, setCreateSubVisible] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [createSubForm] = Form.useForm();

  const loadSubscriptions = useCallback(async () => {
    setSubLoading(true);
    try {
      const res = await SubscriptionService.listSubscriptions(1, 50);
      setSubscriptions(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '获取订阅列表失败');
      setSubscriptions([]);
    } finally {
      setSubLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setPlanLoading(true);
    try {
      const res = await SubscriptionService.listPlans(1, 50);
      setPlans(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '获取订阅计划失败');
      setPlans([]);
    } finally {
      setPlanLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const res = await SubscriptionService.listInvoices(1, 50);
      setInvoices(res.data);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '获取发票列表失败');
      setInvoices([]);
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscriptions();
    loadPlans();
    loadInvoices();
  }, [loadSubscriptions, loadPlans, loadInvoices]);

  const handleCancel = async (id: string) => {
    try {
      await SubscriptionService.cancelSubscription(id, true);
      message.success('已安排在当前周期结束时取消');
      loadSubscriptions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '取消订阅失败');
    }
  };

  const handlePause = async (id: string) => {
    try {
      await SubscriptionService.pauseSubscription(id);
      message.success('订阅已暂停');
      loadSubscriptions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '暂停订阅失败');
    }
  };

  const handleResume = async (id: string) => {
    try {
      await SubscriptionService.resumeSubscription(id);
      message.success('订阅已恢复');
      loadSubscriptions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '恢复订阅失败');
    }
  };

  const handlePayInvoice = async (id: string) => {
    setInvoiceActionId(id);
    try {
      await SubscriptionService.payInvoice(id);
      message.success('发票已支付');
      loadInvoices();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '支付发票失败');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const handleVoidInvoice = async (id: string) => {
    setInvoiceActionId(id);
    try {
      await SubscriptionService.voidInvoice(id);
      message.success('发票已作废');
      loadInvoices();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '作废发票失败');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const openCreateSub = async () => {
    createSubForm.resetFields();
    setCreateSubVisible(true);
    // 懒加载客户列表供选择
    try {
      const res = await CustomerService.listCustomers(1, 100);
      setCustomers(res.data);
    } catch {
      setCustomers([]);
    }
  };

  const handleCreateSub = async (values: any) => {
    setCreatingSub(true);
    try {
      await SubscriptionService.createSubscription({
        customer_id: values.customer_id,
        plan_id: values.plan_id,
        quantity: values.quantity || 1,
        collection_method: values.collection_method || 'charge_automatically',
        days_until_due:
          values.collection_method === 'send_invoice' ? values.days_until_due || 0 : 0,
        trial_period_days: values.trial_period_days || undefined,
      });
      message.success('订阅已创建');
      setCreateSubVisible(false);
      createSubForm.resetFields();
      loadSubscriptions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '创建订阅失败');
    } finally {
      setCreatingSub(false);
    }
  };

  const openEditModal = (sub: Subscription) => {
    setEditingSub(sub);
    editForm.setFieldsValue({
      quantity: sub.quantity ?? 1,
      collection_method: sub.collection_method || 'charge_automatically',
      days_until_due: sub.days_until_due ?? 0,
    });
    setEditModalVisible(true);
  };

  const handleSaveSub = async (values: any) => {
    if (!editingSub) return;
    setSavingSub(true);
    try {
      const collectionMethod = values.collection_method;
      await SubscriptionService.updateSubscription(editingSub.id, {
        quantity: values.quantity,
        collection_method: collectionMethod,
        // 仅在「发送账单」收款方式下才有意义
        days_until_due:
          collectionMethod === 'send_invoice' ? values.days_until_due || 0 : 0,
      });
      message.success('订阅已更新，将在下次续费时生效');
      setEditModalVisible(false);
      setEditingSub(null);
      editForm.resetFields();
      loadSubscriptions();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '更新订阅失败');
    } finally {
      setSavingSub(false);
    }
  };

  const handleCreatePlan = async (values: any) => {
    setCreatingPlan(true);
    try {
      await SubscriptionService.createPlan({
        name: values.name,
        description: values.description,
        amount: yuanToCents(values.amount),
        currency: values.currency,
        interval: values.interval,
        interval_count: values.interval_count || 1,
        trial_period_days: values.trial_period_days || undefined,
      });
      message.success('订阅计划已创建');
      setPlanModalVisible(false);
      planForm.resetFields();
      loadPlans();
    } catch (err: any) {
      message.error(err?.response?.data?.message || '创建订阅计划失败');
    } finally {
      setCreatingPlan(false);
    }
  };

  const subColumns: ColumnsType<Subscription> = [
    {
      title: '订阅ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable={{ text }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text ? `${text.substring(0, 16)}...` : '-'}
        </Text>
      ),
    },
    {
      title: '客户',
      key: 'customer',
      render: (_, r) => r.customer?.name || r.customer?.email || r.customer_id || '-',
    },
    {
      title: '计划',
      key: 'plan',
      render: (_, r) => r.plan?.name || r.plan_id || '-',
    },
    {
      title: '金额',
      key: 'amount',
      render: (_, r) =>
        r.amount != null ? formatAmount(r.amount, r.currency || r.plan?.currency) : '-',
    },
    {
      title: '周期',
      key: 'interval',
      render: (_, r) => {
        const iv = r.interval || r.plan?.interval;
        return iv ? intervalText[iv] || iv : '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const cfg = statusConfig[status] || { color: 'default', text: status || '-' };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: '本期结束',
      dataIndex: 'current_period_end',
      key: 'current_period_end',
      render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, r) => {
        const canceled = r.status === 'canceled' || r.status === 'cancelled';
        const canPause = r.status === 'active' || r.status === 'trialing';
        const canResume = r.status === 'paused';
        return (
          <Space size={0}>
            <Button
              type="link"
              size="small"
              disabled={canceled}
              onClick={() => openEditModal(r)}
            >
              编辑
            </Button>
            {canPause && (
              <Popconfirm
                title="确认暂停该订阅？"
                description="暂停期间不会产生新的扣款，可随时恢复。"
                onConfirm={() => handlePause(r.id)}
                okText="确认暂停"
                cancelText="再想想"
              >
                <Button type="link" size="small">
                  暂停
                </Button>
              </Popconfirm>
            )}
            {canResume && (
              <Button type="link" size="small" onClick={() => handleResume(r.id)}>
                恢复
              </Button>
            )}
            <Popconfirm
              title="确认取消该订阅？"
              description="将在当前计费周期结束时取消。"
              onConfirm={() => handleCancel(r.id)}
              okText="确认取消"
              cancelText="再想想"
              disabled={canceled || r.cancel_at_period_end}
            >
              <Button
                type="link"
                danger
                size="small"
                disabled={canceled || r.cancel_at_period_end}
              >
                {r.cancel_at_period_end ? '待取消' : '取消订阅'}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const planColumns: ColumnsType<SubscriptionPlan> = [
    {
      title: '计划名称',
      dataIndex: 'name',
      key: 'name',
      render: (t: string) => t || '-',
    },
    {
      title: '金额',
      key: 'amount',
      render: (_, r) => formatAmount(r.amount, r.currency),
    },
    {
      title: '周期',
      key: 'interval',
      render: (_, r) =>
        `${intervalText[r.interval] || r.interval}${
          r.interval_count && r.interval_count > 1 ? ` ×${r.interval_count}` : ''
        }`,
    },
    {
      title: '试用天数',
      dataIndex: 'trial_period_days',
      key: 'trial_period_days',
      render: (d: number) => (d ? `${d} 天` : '-'),
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        <Tag color={active === false ? 'default' : 'success'}>
          {active === false ? '已停用' : '启用'}
        </Tag>
      ),
    },
  ];

  const invoiceColumns: ColumnsType<Invoice> = [
    {
      title: '发票ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable={{ text }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text ? `${text.substring(0, 16)}...` : '-'}
        </Text>
      ),
    },
    {
      title: '客户',
      key: 'customer',
      render: (_, r) => r.customer_email || r.customer_id || '-',
    },
    {
      title: '应付金额',
      key: 'amount_due',
      render: (_, r) =>
        r.amount_due != null ? formatAmount(r.amount_due, r.currency) : '-',
    },
    {
      title: '总额',
      key: 'total',
      render: (_, r) => (r.total != null ? formatAmount(r.total, r.currency) : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const cfg = invoiceStatusConfig[status] || { color: 'default', text: status || '-' };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: '到期日',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, r) => {
        const payable = r.status === 'open' || r.status === 'draft';
        const voidable = r.status !== 'paid' && r.status !== 'void';
        const loading = invoiceActionId === r.id;
        return (
          <Space size={0}>
            {r.hosted_invoice_url && (
              <Button
                type="link"
                size="small"
                href={r.hosted_invoice_url}
                target="_blank"
              >
                查看
              </Button>
            )}
            <Popconfirm
              title="确认支付该发票？"
              onConfirm={() => handlePayInvoice(r.id)}
              okText="确认支付"
              cancelText="取消"
              disabled={!payable}
            >
              <Button type="link" size="small" disabled={!payable} loading={loading}>
                支付
              </Button>
            </Popconfirm>
            <Popconfirm
              title="确认作废该发票？"
              description="作废后不可恢复。"
              onConfirm={() => handleVoidInvoice(r.id)}
              okText="确认作废"
              cancelText="取消"
              disabled={!voidable}
            >
              <Button type="link" danger size="small" disabled={!voidable} loading={loading}>
                作废
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            订阅管理
          </Title>
          <Text type="secondary">管理周期性订阅与订阅计划</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadSubscriptions();
              loadPlans();
              loadInvoices();
            }}
          >
            刷新
          </Button>
          <Button icon={<PlusOutlined />} onClick={openCreateSub}>
            创建订阅
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setPlanModalVisible(true)}
          >
            新建订阅计划
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs defaultActiveKey="subscriptions">
          <TabPane tab="订阅列表" key="subscriptions">
            <Table
              rowKey="id"
              columns={subColumns}
              dataSource={subscriptions}
              loading={subLoading}
              locale={{ emptyText: '暂无数据' }}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
              scroll={{ x: 900 }}
            />
          </TabPane>
          <TabPane tab="订阅计划" key="plans">
            <Table
              rowKey="id"
              columns={planColumns}
              dataSource={plans}
              loading={planLoading}
              locale={{ emptyText: '暂无数据' }}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
              scroll={{ x: 700 }}
            />
          </TabPane>
          <TabPane tab="发票" key="invoices">
            <Table
              rowKey="id"
              columns={invoiceColumns}
              dataSource={invoices}
              loading={invoiceLoading}
              locale={{ emptyText: '暂无数据' }}
              pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
              scroll={{ x: 900 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="编辑订阅"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingSub(null);
          editForm.resetFields();
        }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="修改仅影响续费与新订阅"
          description="此处的改动将在下一个计费周期续费时生效，不影响本周期已生效的订阅及已开具的账单/发票。"
        />
        {editingSub && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">订阅计划：</Text>
            <Text strong>{editingSub.plan?.name || editingSub.plan_id || '-'}</Text>
            {editingSub.amount != null && (
              <>
                <br />
                <Text type="secondary">当前金额：</Text>
                <Text>
                  {formatAmount(
                    editingSub.amount,
                    editingSub.currency || editingSub.plan?.currency
                  )}
                </Text>
              </>
            )}
          </div>
        )}
        <Form form={editForm} layout="vertical" onFinish={handleSaveSub}>
          <Form.Item
            label="数量"
            name="quantity"
            tooltip="按订阅计划单价 × 数量计费，调整后于下次续费生效"
            rules={[{ required: true, message: '请输入数量' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="收款方式"
            name="collection_method"
            rules={[{ required: true, message: '请选择收款方式' }]}
          >
            <Select>
              <Select.Option value="charge_automatically">自动扣款</Select.Option>
              <Select.Option value="send_invoice">发送账单（线下付款）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.collection_method !== cur.collection_method
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('collection_method') === 'send_invoice' ? (
                <Form.Item
                  label="账单到期天数"
                  name="days_until_due"
                  tooltip="账单开具后多少天内需付款"
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingSub(null);
                  editForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={savingSub}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新建订阅计划"
        open={planModalVisible}
        onCancel={() => {
          setPlanModalVisible(false);
          planForm.resetFields();
        }}
        footer={null}
        width={560}
      >
        <Form
          form={planForm}
          layout="vertical"
          onFinish={handleCreatePlan}
          initialValues={{ currency: 'USD', interval: 'month', interval_count: 1 }}
        >
          <Form.Item
            label="计划名称"
            name="name"
            rules={[{ required: true, message: '请输入计划名称' }]}
          >
            <Input placeholder="如：专业版月付" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={2} placeholder="选填" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              label="金额"
              name="amount"
              rules={[{ required: true, message: '请输入金额' }]}
            >
              <InputNumber min={0.01} step={0.01} style={{ width: 160 }} placeholder="9.99" />
            </Form.Item>
            <Form.Item
              label="币种"
              name="currency"
              rules={[{ required: true, message: '请选择币种' }]}
            >
              <Select style={{ width: 120 }}>
                <Select.Option value="USD">USD</Select.Option>
                <Select.Option value="EUR">EUR</Select.Option>
                <Select.Option value="GBP">GBP</Select.Option>
                <Select.Option value="CNY">CNY</Select.Option>
              </Select>
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              label="计费周期"
              name="interval"
              rules={[{ required: true, message: '请选择计费周期' }]}
            >
              <Select style={{ width: 160 }}>
                <Select.Option value="day">每日</Select.Option>
                <Select.Option value="week">每周</Select.Option>
                <Select.Option value="month">每月</Select.Option>
                <Select.Option value="year">每年</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="周期数" name="interval_count">
              <InputNumber min={1} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label="试用天数" name="trial_period_days">
              <InputNumber min={0} style={{ width: 120 }} placeholder="0" />
            </Form.Item>
          </Space>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setPlanModalVisible(false);
                  planForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={creatingPlan}>
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="创建订阅"
        open={createSubVisible}
        onCancel={() => {
          setCreateSubVisible(false);
          createSubForm.resetFields();
        }}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={createSubForm}
          layout="vertical"
          onFinish={handleCreateSub}
          initialValues={{ quantity: 1, collection_method: 'charge_automatically' }}
        >
          <Form.Item
            label="客户"
            name="customer_id"
            rules={[{ required: true, message: '请选择客户' }]}
          >
            <Select
              showSearch
              placeholder="选择客户"
              optionFilterProp="children"
              notFoundContent="暂无客户"
            >
              {customers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name || c.email || c.id}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="订阅计划"
            name="plan_id"
            rules={[{ required: true, message: '请选择订阅计划' }]}
          >
            <Select placeholder="选择订阅计划">
              {plans.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name}（{formatAmount(p.amount, p.currency)} /{' '}
                  {intervalText[p.interval] || p.interval}）
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="数量"
            name="quantity"
            rules={[{ required: true, message: '请输入数量' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="收款方式"
            name="collection_method"
            rules={[{ required: true, message: '请选择收款方式' }]}
          >
            <Select>
              <Select.Option value="charge_automatically">自动扣款</Select.Option>
              <Select.Option value="send_invoice">发送账单（线下付款）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.collection_method !== cur.collection_method}
          >
            {({ getFieldValue }) =>
              getFieldValue('collection_method') === 'send_invoice' ? (
                <Form.Item
                  label="账单到期天数"
                  name="days_until_due"
                  tooltip="账单开具后多少天内需付款"
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item label="试用天数" name="trial_period_days">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0（不试用）" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setCreateSubVisible(false);
                  createSubForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={creatingSub}>
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionsPage;
